import {
  IsEventCleaningEmployeeClaim,
  IsVytalEmployee,
  Viewer,
} from '@/providers/ViewerContextProvider';
import { useIsPublicPage } from '@/utils/publicPage';
import { useSidebarContext } from '../SidebarContext';

/**
 * What the sidebar body shows:
 * - 'main'    — the role-based main navigation (Vytal / event-cleaning employees)
 * - 'context' — a per-page context sidebar registered via useRouteSidebar
 * - 'empty'   — no body at all: just the logo and footer user menu, rendered as
 *               a static icon rail (public pages, plus authenticated viewers with
 *               neither the role nav nor a context sidebar, e.g. store terminal /
 *               unrecognized roles). Keeps logout / language reachable everywhere.
 */
export type SidebarBody = 'main' | 'context' | 'empty';

/**
 * Single source of truth for sidebar visibility. The sidebar is ALWAYS rendered
 * (see AppSidebar); these flags only describe its shape, so every consumer reads
 * from here instead of re-deriving the conditions.
 *
 * `isInteractive` gates the app header (sidebar trigger + breadcrumbs) and the
 * collapse rail — true on every authenticated, non-public page. AppShellWrapper
 * only mounts for an authenticated viewer, so the public-page check is the only
 * gate. `body === 'empty'` is equivalent to "neither nav shows", and the shell
 * forces the provider collapsed in that case so the sidebar sits as an icon rail.
 *
 * Context takes priority over the role nav: a page that registers a context
 * sidebar replaces the main nav while it's mounted.
 */
export function useSidebarLayout(viewer: Viewer) {
  const isInteractive = !useIsPublicPage();
  const { contextSidebar } = useSidebarContext();
  const canShowMainNav =
    IsVytalEmployee(viewer) || IsEventCleaningEmployeeClaim(viewer);

  const body: SidebarBody = !isInteractive
    ? 'empty'
    : contextSidebar
      ? 'context'
      : canShowMainNav
        ? 'main'
        : 'empty';

  return { isInteractive, body };
}
